import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogoMark } from "@/components/Navbar";
import Seo from "@/components/Seo";
import ProductForm from "@/components/admin/ProductForm";
import { Link } from "react-router-dom";
import { FileDown, Loader2, LogOut, Pencil, Plus, Search, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = { withCredentials: true };

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-[#0b2545]/20 bg-white text-sm text-[#0b2545] focus:outline-none focus:border-[#d4af37] transition-colors";

function Login({ onLoggedIn }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, auth);
      onLoggedIn(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error("Accesso negato", { description: typeof d === "string" ? d : "Credenziali non valide." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b2545] flex items-center justify-center px-6">
      <div data-testid="admin-login" className="w-full max-w-sm p-8 rounded-3xl bg-[#06152d] border border-[#d4af37]/30 space-y-6">
        <div className="flex items-center gap-3">
          <img src="/images/logo.png" alt="Dahmani Food" className="w-10 h-10 rounded-xl" />
          <div>
            <span className="font-display text-lg text-white block leading-none">DAHMANI FOOD</span>
            <span className="text-[10px] uppercase tracking-widest text-[#d4af37]">Area Riservata</span>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <input data-testid="admin-login-email" type="email" required placeholder="Email" value={email}
            onChange={(e) => setEmail(e.target.value)} className={inputCls} />
          <input data-testid="admin-login-password" type="password" required placeholder="Password" value={password}
            onChange={(e) => setPassword(e.target.value)} className={inputCls} />
          <button data-testid="admin-login-submit" type="submit" disabled={loading}
            className="w-full py-3.5 rounded-xl bg-[#d4af37] text-[#06152d] text-xs font-bold uppercase tracking-widest hover:bg-white transition-colors disabled:opacity-60">
            {loading ? "Accesso…" : "Accedi"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterCat, setFilterCat] = useState("all");
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    const { data } = await axios.get(`${API}/products`);
    setProducts(data.products);
    setCategories(data.categories);
  };

  useEffect(() => {
    axios.get(`${API}/auth/me`, auth)
      .then((r) => { setUser(r.data); load(); })
      .catch(() => setUser(null));
  }, []);

  const visible = useMemo(() => {
    const q = search.toLowerCase();
    return products.filter((p) =>
      (filterCat === "all" || p.category === filterCat) &&
      (!q || p.name.toLowerCase().includes(q))
    );
  }, [products, filterCat, search]);

  const generatePdf = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/admin/catalog/generate`, {}, auth);
      toast.info("Generazione catalogo avviata", { description: `${products.length} prodotti con foto: richiede circa un minuto.` });
      const poll = setInterval(async () => {
        try {
          const { data } = await axios.get(`${API}/catalog/status`);
          if (data.status === "ready") {
            clearInterval(poll);
            setGenerating(false);
            toast.success(`Catalogo pronto: ${data.products} prodotti`, {
              description: "Il pulsante 'Scarica Catalogo PDF' del sito scarica la versione aggiornata.",
            });
          } else if (data.status === "error") {
            clearInterval(poll);
            setGenerating(false);
            toast.error("Generazione fallita", { description: data.error || "Riprova." });
          }
        } catch { /* ignora errori di polling */ }
      }, 4000);
      setTimeout(() => { clearInterval(poll); setGenerating(false); }, 180000);
    } catch (err) {
      setGenerating(false);
      toast.error("Generazione non avviata", { description: err?.response?.data?.detail || "Riprova." });
    }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Eliminare "${p.name}"?`)) return;
    await axios.delete(`${API}/admin/products/${p.id}`, auth);
    toast.success("Prodotto eliminato");
    load();
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, auth);
    setUser(null);
  };

  if (user === undefined) {
    return <div className="min-h-screen bg-[#0b2545] flex items-center justify-center text-[#d4af37]"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }
  if (user === null) return (<><Seo title="Area Riservata" description="Area riservata Dahmani Food." path="/admin" noindex /><Login onLoggedIn={(u) => { setUser(u); load(); }} /></>);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#f0ebe1]">
      <Seo title="Pannello Prodotti | Dahmani Food" description="Area riservata Dahmani Food." path="/admin" noindex />
      <header className="bg-[#0b2545] text-white px-6 py-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/images/logo.png" alt="Dahmani Food" className="w-9 h-9 rounded-lg" />
            <span className="font-display text-lg">Pannello Prodotti</span>
            <span className="text-xs text-white/60 font-mono-df">{products.length} referenze</span>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-generate-pdf" onClick={generatePdf} disabled={generating}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#d4af37] text-[#06152d] text-xs font-bold uppercase tracking-wider hover:bg-white transition-colors disabled:opacity-60">
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
              {generating ? "Generazione…" : "Genera Catalogo PDF"}
            </button>
            <span data-testid="admin-catalog-autonote" className="hidden md:inline text-[10px] text-white/50 leading-tight max-w-[180px]">Il catalogo PDF si aggiorna da solo dopo ogni modifica ai prodotti.</span>
            <Link to="/" data-testid="admin-view-site" className="text-xs text-white/70 hover:text-[#d4af37] uppercase tracking-wider transition-colors">Vedi sito</Link>
            <button data-testid="admin-logout" onClick={logout} className="p-2 text-white/70 hover:text-[#d4af37] transition-colors" aria-label="Esci">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#0b2545]/40" />
            <input data-testid="admin-search" placeholder="Cerca prodotto…" value={search}
              onChange={(e) => setSearch(e.target.value)} className={`${inputCls} pl-10`} />
          </div>
          <select data-testid="admin-filter-category" value={filterCat} onChange={(e) => setFilterCat(e.target.value)}
            className="px-4 py-3 rounded-xl border border-[#0b2545]/20 bg-white text-sm text-[#0b2545] focus:outline-none focus:border-[#d4af37]">
            <option value="all">Tutte le categorie</option>
            {categories.map((c) => (
              <option key={c.slug} value={c.slug}>{c.label}</option>
            ))}
          </select>
          <button data-testid="admin-new-product" onClick={() => { setEditing(null); setFormOpen(true); }}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#0b2545] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#d4af37] hover:text-[#06152d] transition-colors">
            <Plus className="w-4 h-4" /> Nuovo Prodotto
          </button>
        </div>

        <div data-testid="admin-products-grid" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {visible.map((p) => (
            <div key={p.id} data-testid={`admin-product-${p.id}`}
              className="rounded-2xl bg-white border border-[#0b2545]/10 overflow-hidden shadow-sm group">
              <div className="aspect-square bg-white relative">
                {p.image_url ? (
                  <img src={`${API.replace("/api", "")}${p.image_url}`} alt={p.name} loading="lazy" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-[#f5efe4]">
                    <img src="/images/logo.png" alt="" className="w-14 h-14 rounded-xl opacity-60" />
                    <span className="text-[9px] uppercase font-mono-df tracking-widest text-[#0b2545]/40">Da caricare</span>
                  </div>
                )}
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button data-testid={`admin-edit-${p.id}`} onClick={() => { setEditing(p); setFormOpen(true); }}
                    className="p-2 rounded-lg bg-white/95 text-[#0b2545] shadow hover:bg-[#d4af37] transition-colors" aria-label="Modifica">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button data-testid={`admin-delete-${p.id}`} onClick={() => onDelete(p)}
                    className="p-2 rounded-lg bg-white/95 text-red-700 shadow hover:bg-red-600 hover:text-white transition-colors" aria-label="Elimina">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="p-3">
                <p className="text-xs font-bold text-[#0b2545] leading-snug line-clamp-2">{p.name}</p>
                <p className="text-[10px] font-mono-df text-[#c59b27] mt-1">{p.category_label}{p.format ? ` · ${p.format}` : ""}{p.box_qty ? ` · ${p.box_qty} pz/box` : ""}</p>
              </div>
            </div>
          ))}
        </div>
        {visible.length === 0 && (
          <p className="text-center text-sm text-[#0b2545]/60 py-16">Nessun prodotto trovato.</p>
        )}
      </main>

      {formOpen && (
        <ProductForm
          open={formOpen}
          onOpenChange={setFormOpen}
          categories={categories}
          product={editing}
          onSaved={load}
        />
      )}
    </div>
  );
}
